import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';

/**
 * useOnboardingGate — client-side gate mirrored from the backend
 * profile_completion service. Redirects incomplete users to their
 * onboarding flow whenever they enter a dashboard route.
 *
 * Direct URL bypass attempts (e.g. typing /employer/dashboard) are
 * blocked here — this hook fires the redirect on every mount. Backend
 * hiring_access_guard is the authoritative check; this is UX-only.
 */
export function useOnboardingGate() {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/onboarding/state`, {
          withCredentials: true,
        });
        if (cancelled) return;
        setState(res.data);

        const s = res.data || {};
        // Admins skip onboarding.
        if (s.user_type === 'admin') { setChecked(true); return; }
        if (!s.is_complete) {
          const target = s.user_type === 'tradesperson'
            ? '/onboarding/tradesperson'
            : '/onboarding/employer';
          // Tradespeople keep dashboard access while incomplete —
          // the SearchabilityNudge on the dashboard is the canonical
          // reminder. Employers stay strictly gated until complete.
          const allowDashboardWithNudge = (
            s.user_type === 'tradesperson'
            && (location.pathname === '/tradesperson/dashboard'
                || location.pathname.startsWith('/tradesperson/dashboard'))
          );
          if (
            !allowDashboardWithNudge
            && location.pathname !== target
            && !location.pathname.startsWith('/onboarding')
          ) {
            navigate(target, { replace: true });
          }
        } else if (s.user_type === 'employer' && s.approval_status !== 'approved') {
          // Complete but not approved (pending, rejected, suspended).
          // Suspended keeps read-only access to past data, so we only
          // redirect from the dashboard root — nested pages such as
          // /employer/messages remain accessible for read-only view.
          const readOnlyAllowed = ['suspended'].includes(s.approval_status);
          const isDashboardRoot = location.pathname === '/employer/dashboard';
          if (isDashboardRoot || !readOnlyAllowed) {
            if (location.pathname.startsWith('/employer/dashboard')) {
              navigate('/onboarding/employer/pending', { replace: true });
            }
          }
        }
      } catch (_e) { /* auth handled elsewhere */ }
      finally { if (!cancelled) setChecked(true); }
    })();
    return () => { cancelled = true; };
  }, []);

  return { state, checked };
}
